// Rochester Institute of Technology: public college page (#2739), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - RIT's Common Data Set 2025-2026 (Enterprise Data and Analytics PDF,
//    "FINAL 20260603"): C1 applicants, admits and enrollees for Fall 2025
//    with the residency table (the totals come from that table's Total
//    column; C1 gives them by sex only), C8 test policy for Fall 2027 and
//    the score date, C21/C22 plans. Quirk: C21 labels its early decision
//    counts (1,405 applied, 1,028 admitted) "For the Fall 2024 entering
//    class", so they are left out. G1 gives the same 2026-2027 tuition, fees,
//    housing and food as the Tuition and Fees page; that page is used because
//    it itemizes every charge and gives the full cost of attendance.
//  - rit.edu admissions: First-Year Undergraduate Application (application
//    timeline, test-optional policy) and Early Decision. The timeline table
//    carries no year label; it is the page as posted under "Apply for Fall
//    2027", the cycle open now.
//  - College Scorecard API, school id 195003 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'RIT Common Data Set 2025-2026',
        url: 'https://www.rit.edu/institutionalresearch/sites/rit.edu.institutionalresearch/files/documents/Common%20Data%20Set%20PDFs/RIT_CDS_2025_26_FINAL_20260603.pdf',
        read: '2026-10-01',
    },
    apply: {
        label: 'RIT Admissions, First-Year Undergraduate Application',
        url: 'https://www.rit.edu/admissions/first-year-application',
        read: '2026-10-01',
    },
    ed: {
        label: 'RIT Admissions, Early Decision',
        url: 'https://www.rit.edu/admissions/first-year/early-decision',
        read: '2026-10-01',
    },
    costs: {
        label: 'RIT Admissions and Aid, Tuition and Fees 2026-2027',
        url: 'https://www.rit.edu/admissions/tuition-and-fees',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?195003-Rochester-Institute-of-Technology',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'rochester-institute-of-technology',
    kbId: 'rochester_institute_of_technology',
    name: 'Rochester Institute of Technology',
    shortName: 'RIT',
    location: 'Rochester, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (RIT brand portal, colors); decoration only.
    branding: {
        primary: '#F76902',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/rochester_institute_of_technology_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 31565,
        admitted: 22439,
        enrolled: 2959,
        source: 'cds',
        where: 'section C1 (totals from the residency table)',
        residency: [
            { group: 'In-state', applied: 10339, admitted: 6532 },
            { group: 'Out-of-state', applied: 17331, admitted: 13290 },
            { group: 'International', applied: 3895, admitted: 2617 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'RIT\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'The Common Data Set gives early decision counts only for an earlier class and no early action counts, so this page shows no early-round acceptance rate. Early action at RIT is not restrictive.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'Late January', binding: false },
            { plan: 'Early Decision II', deadline: 'January 1', decision: 'Mid-January', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'Mid-March', binding: false },
        ],
        note: 'The fall application opens August 1. The $500 enrollment deposit is due January 15 for Early Decision I, February 15 for Early Decision II and May 1 for Early Action and Regular Decision. RIT recommends filing the FAFSA by November 1 for Early Decision I, January 1 for Early Decision II and January 15 for Early Action and Regular Decision. Applications after the deadline are read on a space-available basis. The Schools of Art, Design, and Film and Animation require a portfolio.',
        source: 'apply',
        binding: 'Early Decision I and II are binding: once you accept and pay the deposit, you withdraw your other applications. You see your scholarship and need-based aid offer before the deposit is due, and if you are admitted to your second-choice program you can ask to be released. Early Action is not binding; you have until May 1.',
        bindingSource: 'ed',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'RIT is test-optional for all undergraduate programs: you choose whether to send SAT or ACT scores, and you can report your scores yourself on the application. If you leave them off, RIT does not see them. RIT superscores the SAT and ACT, and recommends the optional ACT science section for STEM applicants who send the ACT.',
        source: 'apply',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 63508 },
            { item: 'Student activities fee', amount: 470 },
            { item: 'Student health services fee', amount: 630 },
            { item: 'Housing (double room)', amount: 10090 },
            { item: 'Food (standard meal plan)', amount: 7280 },
            { item: 'Books, personal expenses, transportation and loan origination', amount: 2890 },
        ],
        total: 84868,
        note: 'For a new full-time undergraduate living on campus for two semesters. First-year students also pay $430 in orientation fees, which are not in the $84,868 total. Housing and food vary with the room and meal plan. Students must have health insurance.',
        source: 'costs',
        netPrice: {
            average: 37232,
            byIncome: [
                { income: '$0 to $30,000', amount: 24161 },
                { income: '$30,001 to $48,000', amount: 22229 },
                { income: '$48,001 to $75,000', amount: 25290 },
                { income: '$75,001 to $110,000', amount: 32004 },
                { income: 'Over $110,000', amount: 48386 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from RIT\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
